import { z } from "zod";
import { zodQueryStringArray, zodQueryStringBoolean } from "../utils/schema.js";
import { FactNodeSchema } from "./FactNode.js";
import { IdentityEmbedSchema } from "./Identity.js";
import { noteContentMaxLength, NoteContentSchema, NoteSchema } from "./Note.js";
import { defaultTaskBoardColumns } from "./Organization.js";
import {
  PaginationMetaResponseSchema,
  PaginationQuerySchema,
} from "./Pagination.js";
import { TagMetadataSchema, TagSchema } from "./Tag.js";

export const limitTasksPerPage = 100;
export const taskTitleMaxLength = 256;
export const taskDetailsMaxLength = 2048;

const taskBoardColumnIds = defaultTaskBoardColumns.map((c) => c.id) as [
  string,
  ...string[],
];

/**
 * Task assignee types.
 */

export const TaskAssigneeSchema = z.object({
  id: z.uuid(),
  taskId: z.uuid(),
  assigneeId: z.uuid(),
  assignee: IdentityEmbedSchema,
  createdAt: z.date(),
});

export const CreateTaskAssigneeInputSchema = z.object({
  taskId: z.uuid(),
  assigneeId: z.uuid(),
});

export const FilterTaskAssigneeParamsSchema = z.object({
  taskId: z.uuid().optional(),
  assigneeId: z.uuid().optional(),
});

export type TaskAssignee = z.infer<typeof TaskAssigneeSchema>;
export type CreateTaskAssigneeInput = z.infer<
  typeof CreateTaskAssigneeInputSchema
>;
export type FilterTaskAssigneeParams = z.infer<
  typeof FilterTaskAssigneeParamsSchema
>;

/**
 * Core Task types and schemas.
 */

export const TaskSchema = z.object({
  id: z.uuid(),
  title: z.string(),
  details: z.string().nullish(),
  dueDate: z.date().nullable(),
  createdAt: z.date(),
  updatedAt: z.date().nullable(),
  removedAt: z.date().nullable(),
  creatorId: z.uuid(),
  organizationId: z.uuid(),
  private: z.boolean(),
  completed: z.boolean(),
  updatorId: z.string().nullable(),
  createdByAssistant: z.boolean(),
  createdByApiKeyId: z.uuid().nullish(),
  createdByOAuthClientId: z.string().nullish(),
  creator: IdentityEmbedSchema,
  externalId: z.string().nullable(),
  boardColumnId: z.string().nullable(),
  position: z.number(),
  notes: z.array(NoteSchema),
});

export const TaskTagSchema = z.object({
  id: z.uuid(),
  taskId: z.uuid(),
  tagId: z.uuid(),
  createdAt: z.date(),
  tag: z.object({
    id: z.uuid(),
    name: z.string(),
    metadata: TagMetadataSchema,
  }),
});

export const TaskContactSchema = z.object({
  id: z.uuid(),
  taskId: z.uuid(),
  factNodeId: z.uuid(),
  createdAt: z.date(),
  factNode: FactNodeSchema,
});

export const TaskWithRelationsSchema = z.object({
  ...TaskSchema.shape,
  assignees: z.array(TaskAssigneeSchema),
  tags: z.array(TaskTagSchema),
  factNodeTasks: z.array(TaskContactSchema),
});

export const FilterTaskParamsSchema = z.object({
  createdByAssistant: TaskSchema.shape.createdByAssistant.optional(),
  completed: TaskSchema.shape.completed.optional(),
  boardColumnId: z.string().nullish(),
  tagIds: z.array(z.uuid()).optional(),
  assigneeId: z.uuid().optional(),
  contactId: z.uuid().optional(),
  externalId: z.string().optional(),
  dueDateBefore: z.date().optional(),
  dueDateAfter: z.date().optional(),
  dueDateIsNull: z.boolean().optional(),
  search: z.string().trim().max(taskTitleMaxLength).optional(),
  orderBy: z.enum(["updatedAt", "dueDate", "position"]).optional(),
  sortOrder: z.enum(["asc", "desc"]).optional(),
  cursor: z.string().optional(),
  limit: z.int().max(limitTasksPerPage).optional(),
  skip: z.int().optional(),
});

export const doneBoardColumnId = "done";
export const boardOpenTasksLimit = 500;
export const doneTasksPerPage = 25;

export const GetTaskBoardParamsSchema = FilterTaskParamsSchema.pick({
  completed: true,
  tagIds: true,
  assigneeId: true,
  contactId: true,
  dueDateBefore: true,
  dueDateAfter: true,
  dueDateIsNull: true,
  search: true,
});

/** The done column only carries its count; its tasks are paged through `getAll`. */
export const TaskBoardColumnTasksSchema = z.object({
  boardColumnId: z.string().nullable(),
  tasks: z.array(TaskWithRelationsSchema),
  hasMore: z.boolean(),
  totalCount: z.number(),
});

export type GetTaskBoardParams = z.infer<typeof GetTaskBoardParamsSchema>;
export type TaskBoardColumnTasks = z.infer<typeof TaskBoardColumnTasksSchema>;

export const CreateTaskInputSchema = z.object({
  title: z.string().max(taskTitleMaxLength),
  details: z.string().max(taskDetailsMaxLength).optional(),
  dueDate: z.date().nullish(),
  completed: z.boolean().optional(),
  isPrivate: z.boolean().optional(),
  createdByAssistant: z.boolean().optional(),
  createdByConnector: z.string().optional(),
  creatorId: z.string().optional(),
  externalId: z.string().optional(),
  boardColumnId: z.string().nullish(),
  position: z.number().optional(),
});

export const UpdateTaskInputSchema = z.object({
  id: z.uuid(),
  title: z.string().max(taskTitleMaxLength).optional(),
  details: z.string().max(taskDetailsMaxLength).nullish(),
  dueDate: z.date().nullish(),
  completed: z.boolean().optional(),
  isPrivate: z.boolean().optional(),
  removedAt: z.date().nullish(),
  boardColumnId: z.string().nullish(),
  position: z.number().optional(),
});

export type Task = z.infer<typeof TaskSchema>;
export type TaskTag = z.infer<typeof TaskTagSchema>;
export type TaskWithRelations = z.infer<typeof TaskWithRelationsSchema>;
export type FilterTaskParams = z.infer<typeof FilterTaskParamsSchema>;
export type CreateTaskInput = z.infer<typeof CreateTaskInputSchema>;
export type UpdateTaskInput = z.infer<typeof UpdateTaskInputSchema>;
export type TaskContact = z.infer<typeof TaskContactSchema>;

export const CreateTaskToolInputSchema = z.object({
  title: z
    .string()
    .min(3)
    .max(taskTitleMaxLength)
    .describe("A short title for the task."),
  details: z
    .string()
    .max(taskDetailsMaxLength)
    .optional()
    .describe("Additional details or context for this task."),
  dueDate: z.iso.datetime().optional().describe("Due date in ISO 8601 format."),
  private: z
    .boolean()
    .optional()
    .describe("Only the creator can see a private task. Defaults to false."),
});
export const UpdateTaskToolInputSchema = z.object({
  taskId: z.uuid().describe("The task to update."),
  title: CreateTaskToolInputSchema.shape.title.optional(),
  details: z
    .string()
    .max(taskDetailsMaxLength)
    .nullable()
    .optional()
    .describe("New details for the task. Null clears them."),
  dueDate: z.iso
    .datetime()
    .nullable()
    .optional()
    .describe("New due date in ISO 8601 format. Null clears it."),
  completed: z.boolean().optional(),
  private: z
    .boolean()
    .optional()
    .describe("Only the creator can see a private task."),
});
export const CompleteTasksToolInputSchema = z.object({
  taskIds: z.array(z.uuid()).min(1).max(50),
});
export const CompleteTasksToolOutputSchema = z.object({
  completed: z.number().describe("How many tasks were marked complete."),
});
export type CreateTaskToolInput = z.infer<typeof CreateTaskToolInputSchema>;
export type UpdateTaskToolInput = z.infer<typeof UpdateTaskToolInputSchema>;
export type CompleteTasksToolInput = z.infer<
  typeof CompleteTasksToolInputSchema
>;
export type CompleteTasksToolOutput = z.infer<
  typeof CompleteTasksToolOutputSchema
>;

/**
 * ========================================
 * PUBLIC API SCHEMAS
 * ========================================
 */
export const TaskAssigneeItemSchema = IdentityEmbedSchema;
export const TaskTagItemSchema = z.object({
  id: TagSchema.shape.id,
  name: TagSchema.shape.name,
  addedAt: z.date(),
});
export const TaskContactItemSchema = z.object({
  id: FactNodeSchema.shape.id,
  name: z.string().nullable(),
});

export const TaskCommentItemSchema = z.object({
  id: NoteSchema.shape.id,
  content: NoteContentSchema.shape.plainText,
  createdAt: NoteSchema.shape.createdAt,
  creator: IdentityEmbedSchema,
});

export const TaskItemSchema = z.object({
  id: TaskSchema.shape.id,
  title: TaskSchema.shape.title,
  details: TaskSchema.shape.details.nullable(),
  dueDate: TaskSchema.shape.dueDate,
  status: TaskSchema.shape.boardColumnId,
  completed: TaskSchema.shape.completed,
  private: TaskSchema.shape.private,
  externalId: TaskSchema.shape.externalId,
  createdAt: TaskSchema.shape.createdAt,
  updatedAt: TaskSchema.shape.updatedAt.nullable(),
  assignees: z.array(TaskAssigneeItemSchema),
  tags: z.array(TaskTagItemSchema),
  contacts: z.array(TaskContactItemSchema),
  comments: z.array(TaskCommentItemSchema),
});

export const PostTaskRequestSchema = z.object({
  externalId: TaskSchema.shape.externalId.optional(),
  title: z.string().max(taskTitleMaxLength),
  details: z.string().max(taskDetailsMaxLength).optional(),
  dueDate: z.coerce.date().nullish(),
  status: z.enum(taskBoardColumnIds).nullish(),
});

export const PostTaskResponseSchema = z.object({
  task: TaskItemSchema,
});

export const GetTasksRequestSchema = z.object({
  ...PaginationQuerySchema.shape,
  completed: zodQueryStringBoolean(),
  external_id: z.string().optional(),
  tag_id: zodQueryStringArray(z.uuid()),
  status: z
    .preprocess(
      (v) => (v === "null" ? null : v),
      z.enum(taskBoardColumnIds).nullable(),
    )
    .optional(),
});

export const GetTasksResponseSchema = z.object({
  ...PaginationMetaResponseSchema.shape,
  items: z.array(TaskItemSchema),
});

export const PutTaskContactsRequestSchema = z.object({
  contactIds: z.array(z.uuid()),
});

export const PutTaskTagsRequestSchema = z.object({
  tagIds: z.array(z.uuid()),
});

export const PutTaskAssigneesRequestSchema = z.object({
  assigneeIds: z.array(z.uuid()),
});

export const PatchTaskRequestSchema = z.object({
  title: z.string().max(taskTitleMaxLength).optional(),
  details: z.string().max(taskDetailsMaxLength).nullish(),
  dueDate: z.coerce.date().nullish(),
  completed: TaskSchema.shape.completed.optional(),
  status: z.enum(taskBoardColumnIds).nullish(),
});

export const PostTaskCommentRequestSchema = z.object({
  content: NoteContentSchema.shape.plainText.max(noteContentMaxLength),
});

export const PostTaskCommentResponseSchema = z.object({
  comment: TaskCommentItemSchema,
});

export const PatchTaskResponseSchema = z.object({
  task: TaskItemSchema,
});

export type TaskAssigneeItem = z.infer<typeof TaskAssigneeItemSchema>;
export type TaskTagItem = z.infer<typeof TaskTagItemSchema>;
export type TaskContactItem = z.infer<typeof TaskContactItemSchema>;
export type TaskCommentItem = z.infer<typeof TaskCommentItemSchema>;
export type TaskItem = z.infer<typeof TaskItemSchema>;
export type PostTaskRequest = z.infer<typeof PostTaskRequestSchema>;
export type PostTaskResponse = z.infer<typeof PostTaskResponseSchema>;
export type GetTasksRequest = z.infer<typeof GetTasksRequestSchema>;
export type GetTasksResponse = z.infer<typeof GetTasksResponseSchema>;
export type PutTaskContactsRequest = z.infer<
  typeof PutTaskContactsRequestSchema
>;
export type PutTaskTagsRequest = z.infer<typeof PutTaskTagsRequestSchema>;
export type PutTaskAssigneesRequest = z.infer<
  typeof PutTaskAssigneesRequestSchema
>;
export type PatchTaskRequest = z.infer<typeof PatchTaskRequestSchema>;
export type PatchTaskResponse = z.infer<typeof PatchTaskResponseSchema>;
export type PostTaskCommentRequest = z.infer<
  typeof PostTaskCommentRequestSchema
>;
export type PostTaskCommentResponse = z.infer<
  typeof PostTaskCommentResponseSchema
>;
/**
 * ========================================
 * END PUBLIC API SCHEMAS
 * ========================================
 */
